<!--

== Base Input

Props:

    > type
    Tipo do input. Póssiveis: text, number, cel, email, password.

    > label
    Texto descritivo do input, utilizado no label-float.

    > mask
    Usado para passar mascaras no padrão vue-mask.

    > moneyMask
    Usado para passar mascaras no padrão vue-money.

    > masked
    Usado para informar se o v-model deve retornar o valor com mascara ou não.

    > placeholder
    Utilizado para quando não houver label. Utiliza-lo fará com que a props label seja ignorada.

    > max
    Máximo de caracteres

-->

<template>
  <div class="input">
    <div
      class="label-float"
      :class="{ placeholder: placeholder, invalid: !valid }"
    >
      <div v-if="!!search" class="search-field">
        <i class="fal fa-search" style="font-size: 15px; color: #aaaaaaed"></i>
      </div>
      <div class="prefix" v-if="prefix" ref="prefix">
        {{ prefix }}
      </div>
      <input
        ref="inputField"
        :class="`${!!design ? `design-${design}` : ''} ${
          !!search ? 'search' : ''
        }`"
        :id="id"
        :type="!!showPassword ? 'text' : type"
        :placeholder="placeholder ? placeholder : label"
        v-bind="$attrs"
        :maxlength="max"
        v-model="insideValue"
        v-on="inputListeners"
        v-money="moneyAttr"
        v-if="!!moneyAttr"
      />
      <input
        ref="inputField"
        :class="`${!!design ? `design-${design}` : ''} ${
          !!search ? 'search' : ''
        }`"
        :id="id"
        :type="!!showPassword ? 'text' : type"
        :placeholder="placeholder ? placeholder : label"
        v-bind="$attrs"
        :value="value"
        v-on="inputListeners"
        v-mask="maskDefinition"
        v-else
      />
      <label v-if="!placeholder" :for="id">{{ label }}</label>
      <div v-if="type == 'password'" class="eye" @click="toggleEye()">
        <i class="fal" :class="!!showPassword ? 'fa-eye' : 'fa-eye-slash'"></i>
      </div>
    </div>
    <div v-if="options && options.length > 0" class="input-options">
      <ul>
        <li
          v-for="(option, optionIndex) in options"
          :key="optionIndex"
          @click="optionSelected(option, optionIndex)"
        >
          {{ option.title }}
        </li>
      </ul>
    </div>
    <div v-if="optload" class="options-loading">
      <ul>
        <li v-for="li in 2" :key="li">
          <div></div>
          <div></div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { baseEvents } from '@/mixins/baseEvents.js';
import { mask } from 'vue-the-mask';
import { VMoney } from 'v-money';

export default {
  mixins: [baseEvents],
  directives: {
    mask: (el, binding) => {
      if (!binding.value) return;
      mask(el, binding);
    },
    money: VMoney,
  },
  inheritAttrs: false,
  data() {
    let insideValue = null;
    const moneyAttr = this.moneyMask
      ? {
          decimal: ',',
          thousands: '.',
          ...(this.moneyMask === true ? {} : this.moneyMask),
        }
      : false;

    if (moneyAttr) {
      insideValue = (+this.value).toFixed(this.moneyMask.precision || 2);
    } else {
      insideValue = this.value;
    }

    return {
      id: '',
      insideValue,
      moneyAttr,
      nomask: {
        mask: '*'.repeat(255),
        tokens: {
          '*': { pattern: /./ },
        },
      },
      showPassword: false,
    };
  },
  props: {
    type: { type: [String, Array], default: 'text' },
    label: String,
    mask: String | Array,
    moneyMask: { type: [Boolean, Object], default: false },
    options: { type: [Array, Boolean], default: false },
    optload: { type: Boolean, default: false },
    masked: Boolean,
    placeholder: String,
    valid: { type: Boolean, default: true },
    max: { default: 20 },
    search: { type: Boolean, default: null },
    design: { type: String, default: 'two' },
    activeMask: { type: Boolean, default: true },
    prefix: { type: String, default: '' },
  },
  computed: {
    maskDefinition() {
      let definition = null;

      if (this.mask) {
        definition = this.mask;
      } else if (!this.activeMask) {
        definition = '';
      } else {
        definition = this.nomask;
      }
      return definition;
    },
  },
  created() {
    // Gerando ID único
    this.id =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
  },
  mounted() {
    if (this.$refs.prefix)
      this.$refs.inputField.style.paddingLeft = `${
        this.$refs.prefix.clientWidth + 15
      }px`;
  },
  methods: {
    toggleEye() {
      this.showPassword = !this.showPassword;
    },
    focus() {
      this.$refs.inputField.focus();
    },
    optionSelected(option, optionIndex) {
      this.$emit('optionSelected', option, optionIndex);
    },
    unmask(maskedValue, mask) {
      if (this.moneyMask) {
        const negative = maskedValue.indexOf('-') >= 0 ? -1 : 1;

        const regex = /\D+/g;
        let float = null;
        if (regex.test(maskedValue)) {
          const numbers = `${maskedValue}`.replace(regex, '') || '0';
          const exp = Math.pow(10, mask.precision || 0);
          float = parseFloat(numbers) / exp;
        } else {
          float = parseFloat(maskedValue);
        }

        const currency = float.toFixed(mask.precision || 0);
        return `${(parseFloat(currency) * negative).toFixed(
          mask.precision || 0
        )}`;
      } else {
        let unmaskedValue = '';
        let defaultTokens = ['#', 'X', 'S', 'A', 'a', '!'];
        let isToken;

        if (Array.isArray(mask)) {
          let masks = mask
            .filter((m) => m.length >= maskedValue.length)
            .sort((a, b) => a.length <= b.length);
          mask = masks.length > 0 ? masks[0] : mask[0];
        }

        for (let i = 0; i < maskedValue.length; i++) {
          isToken = false;
          for (let j = 0; j < defaultTokens.length; j++) {
            if (mask[i] == defaultTokens[j]) {
              isToken = true;
            }
          }
          if (isToken) {
            unmaskedValue += maskedValue[i];
          }
        }
        return unmaskedValue;
      }
    },
  },
  watch: {
    value: {
      deep: true,
      handler(newV) {
        if (this.moneyMask && !this.masked) {
          const isDecimal = String(newV).includes('.');
          this.insideValue = isDecimal ? parseFloat(newV).toFixed(2) : newV;
        }
      },
    },
    insideValue: {
      deep: true,
      handler(newV) {
        if (this.moneyMask && !this.masked) {
          this.$emit('input', this.unmask(`${newV}`, this.moneyMask));
        }
      },
    },
  },
};
</script>

<style scoped>
.eye {
  position: absolute;
  right: 15px;
  top: 0;
  bottom: 0;
  margin: auto;
  display: flex;
  align-items: center;
}

.eye i {
  color: var(--icon-inactive);
  font-size: 15px;
  cursor: pointer;
}

.prefix {
  background: #45484d73;
  padding: 10px;
  color: var(--label-primary);
  margin-right: 15px;
  font-size: 12px;
  line-height: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 5px;
  height: 36px;
  margin: auto;
}

.search-field {
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  padding: 0 20px;
}

.input {
  padding-top: 10px;
  margin-bottom: 15px;
}

.label-float {
  position: relative;
}

.label-float input {
  background: transparent;
  border: 1px solid var(--input-border);
  padding: 10px 15px;
  height: 45px;
  border-radius: 5px;
  width: 100%;
  font-size: 12px;
  color: var(--input-text);
}

.label-float.placeholder input.search {
  padding-left: 45px;
}

.label-float input.design-one {
  background: transparent;
  border-color: #3a3a40;
}

.label-float input.design-one:not(:placeholder-shown),
.label-float input.design-one:focus {
  background: transparent;
}

.label-float input.design-two {
  background: transparent;
  border-color: var(--input-border);
}

.label-float:not(.placeholder) input::-webkit-input-placeholder {
  opacity: 0;
}
.label-float:not(.placeholder) input::-moz-placeholder {
  opacity: 0;
}
.label-float:not(.placeholder) input:-ms-input-placeholder {
  opacity: 0;
}
.label-float:not(.placeholder) input::-ms-input-placeholder {
  opacity: 0;
}
.label-float:not(.placeholder) input::placeholder {
  opacity: 0;
}

.label-float.placeholder input::-webkit-input-placeholder {
  color: var(--input-text);
  font-size: 12px;
}
.label-float.placeholder input::-moz-placeholder {
  color: var(--input-text);
  font-size: 12px;
}
.label-float.placeholder input:-ms-input-placeholder {
  color: var(--input-text);
  font-size: 12px;
}
.label-float.placeholder input::-ms-input-placeholder {
  color: var(--input-text);
  font-size: 12px;
}
.label-float.placeholder input::placeholder {
  color: var(--input-text);
  font-size: 12px;
}

.label-float label {
  position: absolute;
  padding: 5px 10px;
  left: 5px;
  color: var(--input-text);
  display: flex;
  align-items: center;
  top: 11px;
  font-size: 12px;
  -webkit-transition: 0.2s all ease-in-out;
  -o-transition: 0.2s all ease-in-out;
  transition: 0.2s all ease-in-out;
  background-color: var(--box);
  border-radius: 5px;
  pointer-events: none;
}

.label-float input.design-one + label {
  background-color: transparent;
}

.label-float input:focus + label,
.label-float input:not(:placeholder-shown) + label,
.label-float input:not(:empty) + label {
  top: -12px;
  left: 5px;
  background-color: var(--box);
}

.label-float input.design-one:focus + label,
.label-float input.design-one:not(:placeholder-shown) + label,
.label-float input.design-one:not(:empty) + label {
  background-color: var(--background);
}

.label-float input.design-two:focus + label,
.label-float input.design-two:not(:placeholder-shown) + label,
.label-float input.design-two:not(:empty) + label {
  background-color: var(--box);
}

.label-float input + label:empty {
  display: none;
}

.label-float input:focus,
.label-float.placeholder input.search:focus,
.label-float input[class*='design']:focus {
  outline: 0;
  border-color: var(--default);
}

.label-float input:disabled,
.label-float input:disabled + label {
  cursor: not-allowed;
}

.label-float.invalid input {
  border: 2px solid #9f3041;
}

.label-float.invalid input + label {
  color: #aaaaaaf2;
}

.input-options {
  border: 2px solid #4c4e51;
  border-top: 0;
  cursor: pointer;
}

.input-options li {
  padding: 18px;
  font-size: 1.2em;
  color: #999;
}

.input-options li:not(:last-child) {
  border-bottom: 2px solid #4c4e51;
}

.input-options li:hover {
  filter: brightness(1.1);
}

.options-loading li {
  height: 48px;
  width: 100%;
  border: 1px solid #4c4e51;
  padding: 0 18px;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
}

.options-loading li div {
  height: 5px;
  width: 35%;
  -webkit-animation-name: example;
  animation-name: example;
  -webkit-animation-duration: 1s;
  animation-duration: 1s;
  -webkit-animation-iteration-count: infinite;
  animation-iteration-count: infinite;
  -webkit-animation-timing-function: ease-in-out;
  animation-timing-function: ease-in-out;
  animation-direction: alternate-reverse;
}

.options-loading li div:first-child {
  width: 15px;
  margin-right: 10px;
}

@-webkit-keyframes example {
  from {
    background-color: #4c4e51;
  }
  to {
    background-color: #3f4246;
  }
}

@keyframes example {
  from {
    background-color: #4c4e51;
  }
  to {
    background-color: #3f4246;
  }
}

/* Casos especificos */

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
}
</style>
